import { F_MapMarkerLineFragment } from '__generated__v2/graphql';
import { isNil, omitBy } from 'lodash';
import { LineLayout, LinePaint } from 'mapbox-gl';
import { useCtxMapView } from 'pages/special/OsloDesignFair/map/CtxMapView';
import { EmptyLayer } from 'pages/special/OsloDesignFair/map/markers/view/EmptyLayer';
import { Layer, Source } from 'react-map-gl';

interface Props {
    marker: F_MapMarkerLineFragment;

    beforeId?: string;

    orderIndex: number;
    isHighlighted: boolean;
}

export const LineMarker = (props: Props): JSX.Element => {
    const ctxMapView = useCtxMapView();
    const marker = props.marker;
    const markerId = props.marker.id;

    const paintAttributes: LinePaint = {
        'line-color': marker.color,
        'line-width': marker.width,
        'line-opacity': marker.opacity,
        'line-dasharray':
            marker.isDashed && marker.dashedLineLength && marker.dashedGapLength
                ? [marker.dashedLineLength, marker.dashedGapLength]
                : undefined
    };

    const layoutAttributes: LineLayout = {
        visibility: !props.marker.isHidden ? 'visible' : 'none'
    };

    const layerIds = {
        layer: markerId + '|layer',
        layerClick: markerId + '|clickable',
        highlight1: markerId + '|highlight1',
        highlight2: markerId + '|highlight2',
        last: markerId + '|last'
    };

    const beforeIds = {
        layer: props.beforeId,
        layerClick: layerIds.layer,
        highlight1: layerIds.layerClick,
        highlight2: layerIds.highlight1,
        last: layerIds.highlight2
    };

    return (
        <Source
            id={markerId}
            type="geojson"
            data={{
                type: 'Feature',
                properties: {
                    orderIndex: props.orderIndex,
                    markerId
                },
                geometry: {
                    type: 'LineString',
                    coordinates: marker.coordinates.map(({ lng, lat }) => [lng, lat])
                }
            }}
        >
            <Layer
                id={layerIds.layer}
                beforeId={beforeIds.layer}
                type="line"
                source={markerId}
                paint={{ ...omitBy(paintAttributes, isNil) }}
                layout={{ ...omitBy(layoutAttributes, isNil) }}
            />
            {props.marker.selectable ? (
                <Layer
                    id={layerIds.layerClick}
                    beforeId={beforeIds.layerClick}
                    type="line"
                    source={markerId}
                    paint={{
                        ...omitBy(paintAttributes, isNil),
                        'line-opacity': 0,
                        'line-width': Math.max(marker.width, 30)
                    }}
                    layout={{ ...omitBy(layoutAttributes, isNil) }}
                />
            ) : (
                <EmptyLayer id={layerIds.layerClick} beforeId={beforeIds.layerClick} />
            )}
            {props.isHighlighted ? (
                <div>
                    <Layer
                        id={layerIds.highlight1}
                        beforeId={beforeIds.highlight1}
                        type="line"
                        source={markerId}
                        paint={{
                            ...omitBy(paintAttributes, isNil),
                            'line-width': Math.max(Number(paintAttributes['line-width']) / 6, 3),
                            'line-color': '#F8E71C',
                            'line-opacity': 1,
                            'line-offset': -Number(paintAttributes['line-width']) / 2,
                            'line-blur': 4
                        }}
                        layout={{ ...omitBy(layoutAttributes, isNil) }}
                    />
                    <Layer
                        id={layerIds.highlight2}
                        beforeId={beforeIds.highlight2}
                        type="line"
                        source={markerId}
                        paint={{
                            ...omitBy(paintAttributes, isNil),
                            'line-width': Math.max(Number(paintAttributes['line-width']) / 6, 3),
                            'line-color': '#F8E71C',
                            'line-opacity': 1,
                            'line-offset': Number(paintAttributes['line-width']) / 2,
                            'line-blur': 4
                        }}
                        layout={{ ...omitBy(layoutAttributes, isNil) }}
                    />
                </div>
            ) : (
                <div>
                    <EmptyLayer id={layerIds.highlight1} beforeId={beforeIds.highlight1} />
                    <EmptyLayer id={layerIds.highlight2} beforeId={beforeIds.highlight2} />
                </div>
            )}

            <EmptyLayer id={layerIds.last} beforeId={beforeIds.last} />
        </Source>
    );
};
